// =============================================================================
// CALL REACTION BURST
// =============================================================================
// Animates ephemeral in-call reactions (own taps + relayed peer taps) as emoji
// that float up and fade. Each burst removes itself via onDone when its
// animation finishes; nothing here is persisted.

import { useEffect, useRef } from 'react';
import type { CallReaction } from '../../stores/callStore.js';

// How long a single burst stays on screen. Kept in sync with the animation
// duration below so onDone fires as the emoji finishes fading.
const BURST_DURATION_MS = 2600;

interface CallReactionBurstProps {
  reactions: CallReaction[];
  onDone: (id: string) => void;
}

export function CallReactionBurst({ reactions, onDone }: CallReactionBurstProps) {
  return (
    <div className="pointer-events-none absolute inset-0 z-20 overflow-hidden">
      {reactions.map((reaction) => (
        <BurstItem key={reaction.id} reaction={reaction} onDone={onDone} />
      ))}
      <style>{`
        @keyframes call-reaction-rise {
          0% { transform: translateY(0) scale(0.6); opacity: 0; }
          15% { transform: translateY(-10%) scale(1.1); opacity: 1; }
          70% { opacity: 1; }
          100% { transform: translateY(-90%) scale(1); opacity: 0; }
        }
        .call-reaction-emoji {
          animation: call-reaction-rise ${BURST_DURATION_MS}ms ease-out both;
        }
        @media (prefers-reduced-motion: reduce) {
          .call-reaction-emoji { animation: call-reaction-fade ${BURST_DURATION_MS}ms linear both; }
          @keyframes call-reaction-fade {
            0% { opacity: 0; }
            10% { opacity: 1; }
            80% { opacity: 1; }
            100% { opacity: 0; }
          }
        }
      `}</style>
    </div>
  );
}

interface BurstItemProps {
  reaction: CallReaction;
  onDone: (id: string) => void;
}

function BurstItem({ reaction, onDone }: BurstItemProps) {
  // A stable horizontal offset per burst so simultaneous reactions fan out
  // instead of stacking. Own taps drift right, peer taps drift left, giving a
  // light "mine vs theirs" sense without labelling each emoji.
  const left = useRef(
    reaction.fromSelf ? 58 + Math.random() * 24 : 18 + Math.random() * 24
  );

  useEffect(() => {
    const timer = setTimeout(() => { onDone(reaction.id); }, BURST_DURATION_MS);
    return () => { clearTimeout(timer); };
  }, [reaction.id, onDone]);

  return (
    <span
      className="call-reaction-emoji absolute bottom-24 text-5xl drop-shadow-lg"
      style={{ left: `${left.current}%` }}
      aria-hidden="true"
    >
      {reaction.emoji}
    </span>
  );
}
